<template>
  <div class="conversation-list flex flex-col h-full">
    <!-- Header -->
    <div
      class="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700"
    >
      <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-200">Conversations</h4>
      <button
        @click="$emit('new-conversation')"
        class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-500 dark:text-gray-400 transition-colors"
        title="New conversation"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-4 w-4"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
            clip-rule="evenodd"
          />
        </svg>
      </button>
    </div>

    <!-- List -->
    <div class="flex-1 overflow-y-auto">
      <!-- Loading state -->
      <div v-if="chatStore.conversationsLoading" class="p-4 text-center">
        <DotPulseLoader color="#8B5CF6" :customColor="true" />
      </div>

      <!-- Empty state -->
      <div
        v-else-if="chatStore.conversations.length === 0"
        class="p-4 text-center text-sm text-gray-400 dark:text-gray-500"
      >
        No previous conversations
      </div>

      <!-- Conversation items -->
      <div v-else class="py-1">
        <button
          v-for="conv in chatStore.conversations"
          :key="conv.id"
          @click="$emit('select-conversation', conv.id)"
          class="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors border-b border-gray-100 dark:border-gray-800 group"
          :class="{
            'bg-eggplant-50/50 dark:bg-eggplant-900/20': chatStore.conversationId === conv.id,
          }"
        >
          <p
            class="text-sm font-medium text-gray-700 dark:text-gray-200 truncate group-hover:text-eggplant-600 dark:group-hover:text-eggplant-400"
          >
            {{ conv.title || "Untitled conversation" }}
          </p>
          <div class="flex items-center gap-2 mt-1">
            <span class="text-xs text-gray-400 dark:text-gray-500">
              {{ formatDate(conv.updatedAt) }}
            </span>
            <span class="text-xs text-gray-300 dark:text-gray-600">
              {{ conv._count.messages }} messages
            </span>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import DotPulseLoader from "@/components/chat/DotPulseLoader.vue";
import { useChatStore } from "@/stores/chatStore";

defineEmits<{
  "new-conversation": [];
  "select-conversation": [conversationId: string];
}>();

const chatStore = useChatStore();

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
</script>
